import { request } from "./client";
import { env } from "@/lib/env";
import type {
  WaitlistSubmission,
  WaitlistResponse,
  WaitlistConfirmInput,
  WaitlistConfirmResponse,
  InvestorDeckSubmission,
  InvestorDeckResponse,
  CollaboratorSubmission,
  CollaboratorResponse,
  NewsletterSubmission,
  NewsletterResponse,
  ContactSubmission,
  FormConsentRecord,
  ContactRequest,
  ContactResponse,
  PartnerSubmission,
  PartnerResponse,
} from "./types";

async function loadFirestoreHandlers() {
  return import("./firestore");
}

/**
 * Function-per-endpoint surface. Components import these (never the raw
 * `request` helper) so swapping providers later is a one-file change.
 *
 * Paths are relative — the env-derived base URL is glued on inside
 * `request()`.
 */

export function joinWaitlist(
  input: WaitlistSubmission,
  consentRecord: FormConsentRecord,
  signal?: AbortSignal,
): Promise<WaitlistResponse> {
  if (env.useFirestore) {
    return loadFirestoreHandlers().then((handlers) =>
      handlers.firestoreJoinWaitlist(input, consentRecord, signal),
    );
  }

  return request<WaitlistResponse, WaitlistSubmission & { consentRecord: FormConsentRecord }>(
    "/v1/waitlist",
    {
      method: "POST",
      body: { ...input, consentRecord },
      signal,
    },
  );
}

export function confirmWaitlist(
  input: WaitlistConfirmInput,
  signal?: AbortSignal,
): Promise<WaitlistConfirmResponse> {
  if (env.useFirestore) {
    return loadFirestoreHandlers().then((handlers) =>
      handlers.firestoreConfirmWaitlist(input, signal),
    );
  }

  return request<WaitlistConfirmResponse, WaitlistConfirmInput>(
    "/v1/waitlist/confirm",
    {
      method: "POST",
      body: input,
      signal,
    },
  );
}

export function requestInvestorDeck(
  input: InvestorDeckSubmission,
  consentRecord: FormConsentRecord,
  signal?: AbortSignal,
): Promise<InvestorDeckResponse> {
  if (env.useFirestore) {
    return loadFirestoreHandlers().then((handlers) =>
      handlers.firestoreRequestInvestorDeck(input, consentRecord, signal),
    );
  }

  return request<
    InvestorDeckResponse,
    InvestorDeckSubmission & { consentRecord: FormConsentRecord }
  >("/v1/investor-deck", {
    method: "POST",
    body: { ...input, consentRecord },
    signal,
  });
}

export function submitCollaborator(
  input: CollaboratorSubmission,
  consentRecord: FormConsentRecord,
  signal?: AbortSignal,
): Promise<CollaboratorResponse> {
  if (env.useFirestore) {
    return loadFirestoreHandlers().then((handlers) =>
      handlers.firestoreSubmitCollaborator(input, consentRecord, signal),
    );
  }

  return request<
    CollaboratorResponse,
    CollaboratorSubmission & { consentRecord: FormConsentRecord }
  >("/v1/collaborators", {
    method: "POST",
    body: { ...input, consentRecord },
    signal,
  });
}

export function subscribeNewsletter(
  input: NewsletterSubmission,
  consentRecord: FormConsentRecord,
  signal?: AbortSignal,
): Promise<NewsletterResponse> {
  if (env.useFirestore) {
    return loadFirestoreHandlers().then((handlers) =>
      handlers.firestoreSubscribeNewsletter(input, consentRecord, signal),
    );
  }

  return request<
    NewsletterResponse,
    NewsletterSubmission & { consentRecord: FormConsentRecord }
  >("/v1/newsletter", {
    method: "POST",
    body: { ...input, consentRecord },
    signal,
  });
}

export function submitContact(
  input: ContactSubmission & {
    attribution?: ContactRequest["attribution"];
    entryPoint?: string;
    source?: string;
  },
  consentRecord: FormConsentRecord,
  signal?: AbortSignal,
): Promise<ContactResponse> {
  if (env.useFirestore) {
    return loadFirestoreHandlers().then((handlers) =>
      handlers.firestoreSubmitContact(input, consentRecord, signal),
    );
  }

  const { attribution, entryPoint, source, ...submission } = input;
  const payload: ContactRequest = {
    ...submission,
    consentRecord,
    attribution,
    entryPoint,
    source,
  };

  return request<ContactResponse, ContactRequest>("/v1/contact", {
    method: "POST",
    body: payload,
    signal,
  });
}

export function submitPartner(
  input: PartnerSubmission,
  consentRecord: FormConsentRecord,
  signal?: AbortSignal,
): Promise<PartnerResponse> {
  if (env.useFirestore) {
    return loadFirestoreHandlers().then((handlers) =>
      handlers.firestoreSubmitPartner(input, consentRecord, signal),
    );
  }

  return request<PartnerResponse, PartnerSubmission & { consentRecord: FormConsentRecord }>(
    "/v1/partners",
    {
      method: "POST",
      body: { ...input, consentRecord },
      signal,
    },
  );
}

export type {
  WaitlistInput,
  WaitlistSubmission,
  WaitlistResponse,
  WaitlistConfirmInput,
  WaitlistConfirmResponse,
  InvestorDeckInput,
  InvestorDeckSubmission,
  InvestorDeckResponse,
  CollaboratorInput,
  CollaboratorSubmission,
  CollaboratorResponse,
  NewsletterInput,
  NewsletterSubmission,
  NewsletterResponse,
  ContactInput,
  ContactSubmission,
  FormConsentRecord,
  ContactConsentRecord,
  ContactResponse,
  PartnerInput,
  PartnerSubmission,
  PartnerResponse,
} from "./types";
